import React, { useState, useEffect } from 'react';
import { Users, UserCheck, Shield, Plus, Search, Eye, EyeOff, Key, Power, LogOut as LogOutIcon, Edit, Trash2, Copy, Link2, CheckCircle, XCircle, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { ClientDirectory } from '../components/client-channels/ClientDirectory';
import { loadUserDirectory } from '../components/client-channels/api';

const API = process.env.REACT_APP_BACKEND_URL;

const UserManagement = () => {
  const [activeTab, setActiveTab] = useState('clientes'); // 'clientes' | 'auditores' | 'perfil'
  const [clientes, setClientes] = useState([]);
  const [auditores, setAuditores] = useState([]);
  const [carteras, setCarteras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');

  // Dialogs
  const [showCreateClienteDialog, setShowCreateClienteDialog] = useState(false);
  const [showEditClienteDialog, setShowEditClienteDialog] = useState(false);
  const [showCreateAuditorDialog, setShowCreateAuditorDialog] = useState(false);
  const [showCredentialsDialog, setShowCredentialsDialog] = useState(false);
  const [showResetPasswordDialog, setShowResetPasswordDialog] = useState(false);
  const [showChangeUsernameDialog, setShowChangeUsernameDialog] = useState(false);
  const [showChangePasswordDialog, setShowChangePasswordDialog] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [newCredentials, setNewCredentials] = useState(null);

  // Form states
  const [clienteForm, setClienteForm] = useState({
    name: '',
    cartera_id: '',
    username: '',
    password: '',
    confirmPassword: '',
    telegram_user_id: ''  // ID de Telegram del usuario
  });

  const [auditorForm, setAuditorForm] = useState({
    full_name: '',
    email: '',
    username: '',
    password: '',
    confirmPassword: ''
  });

  const [showClientePassword, setShowClientePassword] = useState(false);
  const [showClienteConfirmPassword, setShowClienteConfirmPassword] = useState(false);

  const [resetPasswordForm, setResetPasswordForm] = useState({
    newPassword: '',
    confirmPassword: ''
  });

  // Admin profile forms
  const [changeUsernameForm, setChangeUsernameForm] = useState({
    newUsername: ''
  });

  const [changePasswordForm, setChangePasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async (silent = false) => {
    if (!silent) setLoading(true);
    setLoadError('');
    try {
      const data = await loadUserDirectory();
      setCarteras(data.carteras);
      setClientes(data.clientes);
      setAuditores(data.auditores);

    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Error al cargar datos');
      setLoadError(error.message || 'No se pudieron cargar los datos.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateCliente = async (e) => {
    e.preventDefault();
    
    if (clienteForm.password !== clienteForm.confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/clientes`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          name: clienteForm.name,
          cartera_id: clienteForm.cartera_id,
          username: clienteForm.username,
          password: clienteForm.password,
          telegram_user_id: clienteForm.telegram_user_id || null  // ID de Telegram
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al crear cliente');
      }

      const data = await response.json();
      
      // Mostrar credenciales
      setNewCredentials({
        type: 'cliente',
        username: data.username,
        password: clienteForm.password
      });
      
      setShowCredentialsDialog(true);
      setShowCreateClienteDialog(false);
      setClienteForm({
        name: '',
        cartera_id: '',
        username: '',
        password: '',
        confirmPassword: '',
        telegram_user_id: ''
      });
      
      loadData();
      toast.success('Cliente creado exitosamente');
    } catch (error) {
      console.error('Error creating cliente:', error);
      toast.error(error.message);
    }
  };

  const handleUpdateCliente = async (e) => {
    e.preventDefault();
    
    // Si se ingresó contraseña, validar que coincidan
    if (clienteForm.password && clienteForm.password !== clienteForm.confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const updateData = {
        name: clienteForm.name,
        telegram_user_id: clienteForm.telegram_user_id || null
      };
      
      // Solo incluir password si se ingresó una nueva
      if (clienteForm.password) {
        updateData.password = clienteForm.password;
      }
      
      const response = await fetch(`${API}/api/clientes/${selectedUser.id}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updateData)
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al actualizar cliente');
      }
      
      setShowEditClienteDialog(false);
      setClienteForm({
        name: '',
        cartera_id: '',
        username: '',
        password: '',
        confirmPassword: '',
        telegram_user_id: ''
      });
      setSelectedUser(null);
      
      loadData();
      toast.success('Cliente actualizado exitosamente');
    } catch (error) {
      console.error('Error updating cliente:', error);
      toast.error(error.message);
    }
  };

  const handleCreateAuditor = async (e) => {
    e.preventDefault();
    
    if (auditorForm.password !== auditorForm.confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/auditores`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          full_name: auditorForm.full_name,
          email: auditorForm.email,
          username: auditorForm.username,
          password: auditorForm.password
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al crear auditor');
      }

      const data = await response.json();
      
      // Mostrar credenciales
      setNewCredentials({
        type: 'auditor',
        username: data.username,
        password: auditorForm.password,
        email: data.email,
        full_name: data.full_name
      });
      
      setShowCredentialsDialog(true);
      setShowCreateAuditorDialog(false);
      setAuditorForm({
        full_name: '',
        email: '',
        username: '',
        password: '',
        confirmPassword: '',
        telegram_user_id: ''
      });
      
      loadData();
      toast.success('Auditor creado exitosamente');
    } catch (error) {
      console.error('Error creating auditor:', error);
      toast.error(error.message);
    }
  };

  const handleToggleStatus = async (userId, userType) => {
    try {
      const token = localStorage.getItem('token');
      const endpoint = userType === 'cliente' ? 'clientes' : 'auditores';
      
      const response = await fetch(`${API}/api/${endpoint}/${userId}/toggle-status`, {
        method: 'PATCH',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Error al cambiar estado');

      const data = await response.json();
      toast.success(data.message);
      loadData();
    } catch (error) {
      console.error('Error toggling status:', error);
      toast.error('Error al cambiar estado');
    }
  };

  const handleCloseSessions = async (userId, userType) => {
    try {
      const token = localStorage.getItem('token');
      const endpoint = userType === 'cliente' ? 'clientes' : 'auditores';
      
      const response = await fetch(`${API}/${endpoint}/${userId}/close-sessions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Error al cerrar sesiones');

      const data = await response.json();
      toast.success(data.message);
      loadData();
    } catch (error) {
      console.error('Error closing sessions:', error);
      toast.error('Error al cerrar sesiones');
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (resetPasswordForm.newPassword !== resetPasswordForm.confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const endpoint = selectedUser.type === 'cliente' ? 'clientes' : 'auditores';
      
      const response = await fetch(`${API}/api/${endpoint}/${selectedUser.id}/reset-password?new_password=${encodeURIComponent(resetPasswordForm.newPassword)}`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Error al cambiar contraseña');

      const data = await response.json();
      toast.success(data.message);
      setShowResetPasswordDialog(false);
      setResetPasswordForm({ newPassword: '', confirmPassword: '' });
      setSelectedUser(null);
      loadData();
    } catch (error) {
      console.error('Error resetting password:', error);
      toast.error('Error al cambiar contraseña');
    }
  };

  const handleDelete = async (userId, userType, userName) => {
    if (!window.confirm(`¿Estás seguro de eliminar ${userType === 'cliente' ? 'el cliente' : 'el auditor'} "${userName}"?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const endpoint = userType === 'cliente' ? 'clientes' : 'auditores';
      
      const response = await fetch(`${API}/api/${endpoint}/${userId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) throw new Error('Error al eliminar');

      toast.success(`${userType === 'cliente' ? 'Cliente' : 'Auditor'} eliminado exitosamente`);
      loadData();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Error al eliminar');
    }
  };

  const handleChangeUsername = async (e) => {
    e.preventDefault();
    
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/auth/update-username?new_username=${encodeURIComponent(changeUsernameForm.newUsername)}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al actualizar username');
      }

      const data = await response.json();
      toast.success(data.message);
      setShowChangeUsernameDialog(false);
      setChangeUsernameForm({ newUsername: '' });
      
      // Actualizar username en localStorage
      const user = JSON.parse(localStorage.getItem('user') || '{}');
      user.username = data.new_username;
      localStorage.setItem('user', JSON.stringify(user));
    } catch (error) {
      console.error('Error changing username:', error);
      toast.error(error.message);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    
    if (changePasswordForm.newPassword !== changePasswordForm.confirmPassword) {
      toast.error('Las contraseñas no coinciden');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/api/auth/update-password?current_password=${encodeURIComponent(changePasswordForm.currentPassword)}&new_password=${encodeURIComponent(changePasswordForm.newPassword)}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || 'Error al actualizar contraseña');
      }

      const data = await response.json();
      toast.success(data.message);
      setShowChangePasswordDialog(false);
      setChangePasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    } catch (error) {
      console.error('Error changing password:', error);
      toast.error(error.message);
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copiado al portapapeles`);
  };

  const copyAuditorLink = (auditor) => {
    const baseUrl = window.location.origin;
    const auditorLink = `${baseUrl}/auditor/login`;
    copyToClipboard(auditorLink, 'Link de acceso del auditor');
    toast.info(`Usuario: ${auditor.username}`, { duration: 3000 });
  };

  const filteredClientes = clientes.filter(c => 
    c.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.username.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredAuditores = auditores.filter(a => 
    a.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
    a.email.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
      </div>
    );
  }

  // El flujo comercial es independiente de los formularios de credenciales.
  // El perfil del operador conserva su comportamiento actual.
  if (activeTab === 'clientes') {
    return <>{loadError && <p data-testid="client-directory-load-error" role="alert" className="px-6 pt-4 text-sm text-red-600">{loadError}</p>}<ClientDirectory clients={clientes} carteras={carteras}
      onRefresh={() => loadData(true)} onProfile={() => setActiveTab('perfil')} /></>;
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-3">
          <Users className="w-8 h-8" />
          Gestión de Usuarios
        </h1>
        <p className="text-gray-600 dark:text-gray-400 mt-2">
          Administra clientes y auditores del sistema
        </p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 border-b border-gray-200 dark:border-gray-700">
        <button
          onClick={() => setActiveTab('clientes')}
          className={`px-6 py-3 font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'clientes'
              ? 'text-indigo-600 border-b-2 border-indigo-600'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900'
          }`}
        >
          <UserCheck className="w-5 h-5" />
          Clientes ({clientes.length})
        </button>
        <button
          onClick={() => setActiveTab('auditores')}
          className={`px-6 py-3 font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'auditores'
              ? 'text-indigo-600 border-b-2 border-indigo-600'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900'
          }`}
        >
          <Shield className="w-5 h-5" />
          Auditores ({auditores.length})
        </button>
        <button
          onClick={() => setActiveTab('perfil')}
          className={`px-6 py-3 font-medium transition-colors flex items-center gap-2 ${
            activeTab === 'perfil'
              ? 'text-green-600 border-b-2 border-green-600'
              : 'text-gray-600 dark:text-gray-400 hover:text-gray-900'
          }`}
        >
          <Key className="w-5 h-5" />
          Mi Perfil (Admin)
        </button>
      </div>

      {/* Actions Bar */}
      <div className="flex justify-between items-center mb-6">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
          <input
            type="text"
            placeholder="Buscar..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-800 dark:text-white"
          />
        </div>
        
        {activeTab === 'clientes' ? (
          <button
            onClick={() => setShowCreateClienteDialog(true)}
            className="ml-4 px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Nuevo Cliente
          </button>
        ) : (
          <button
            onClick={() => setShowCreateAuditorDialog(true)}
            className="ml-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 flex items-center gap-2"
          >
            <Plus className="w-5 h-5" />
            Nuevo Auditor
          </button>
        )}
      </div>

      {/* Table - Clientes */}
      {activeTab === 'clientes' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Usuario
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Nombre
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Telegram ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Cartera
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Balance
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Sesión
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredClientes.map((cliente) => (
                <tr key={cliente.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleToggleStatus(cliente.id, 'cliente')}
                      className="text-2xl"
                      title={cliente.is_active ? 'Activo - Click para desactivar' : 'Inactivo - Click para activar'}
                    >
                      {cliente.is_active ? '🟢' : '🔴'}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {cliente.username}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white">{cliente.name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {cliente.telegram_user_id ? (
                      <div className="flex items-center gap-1">
                        <span className="text-xs font-mono bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">
                          {cliente.telegram_user_id}
                        </span>
                        <span className="text-green-500" title="Vinculado con Telegram">🤖</span>
                      </div>
                    ) : (
                      <span className="text-xs text-gray-400 dark:text-gray-500">No vinculado</span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-600 dark:text-gray-400">{cliente.cartera_name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-semibold text-gray-900 dark:text-white">
                      ${cliente.balance.toLocaleString('es-AR', { minimumFractionDigits: 2 })}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {cliente.has_active_session ? (
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                        Activa
                      </span>
                    ) : (
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                        Cerrada
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => {
                          setClienteForm({
                            cartera_id: cliente.cartera_id,
                            name: cliente.name,
                            username: cliente.username,
                            password: '',
                            confirmPassword: '',
                            telegram_user_id: cliente.telegram_user_id || ''
                          });
                          setSelectedUser({ id: cliente.id, type: 'cliente', name: cliente.name });
                          setShowEditClienteDialog(true);
                        }}
                        className="text-blue-600 hover:text-blue-900"
                        title="Editar cliente"
                      >
                        <Edit className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedUser({ id: cliente.id, type: 'cliente', name: cliente.name, username: cliente.username });
                          setShowResetPasswordDialog(true);
                        }}
                        className="text-amber-600 hover:text-amber-900"
                        title="Cambiar contraseña"
                      >
                        <Key className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleCloseSessions(cliente.id, 'cliente')}
                        className="text-orange-600 hover:text-orange-900"
                        title="Cerrar sesión"
                      >
                        <LogOutIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(cliente.id, 'cliente', cliente.name)}
                        className="text-red-600 hover:text-red-900"
                        title="Eliminar"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Table - Auditores */}
      {activeTab === 'auditores' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
          <table className="w-full">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Estado
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Usuario
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Nombre Completo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Email
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Sesión
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {filteredAuditores.map((auditor) => (
                <tr key={auditor.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <button
                      onClick={() => handleToggleStatus(auditor.id, 'auditor')}
                      className="text-2xl"
                      title={auditor.is_active ? 'Activo - Click para desactivar' : 'Inactivo - Click para activar'}
                    >
                      {auditor.is_active ? '🟢' : '🔴'}
                    </button>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900 dark:text-white">
                      {auditor.username}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-white">{auditor.full_name}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-600 dark:text-gray-400">{auditor.email}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    {auditor.has_active_session ? (
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-green-100 text-green-800">
                        Activa
                      </span>
                    ) : (
                      <span className="px-2 py-1 text-xs font-semibold rounded-full bg-gray-100 text-gray-800">
                        Cerrada
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex justify-end gap-2">
                      <button
                        onClick={() => copyAuditorLink(auditor)}
                        className="text-blue-600 hover:text-blue-900"
                        title="Copiar link de acceso"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          setSelectedUser({ id: auditor.id, type: 'auditor', name: auditor.full_name, username: auditor.username });
                          setShowResetPasswordDialog(true);
                        }}
                        className="text-amber-600 hover:text-amber-900"
                        title="Cambiar contraseña"
                      >
                        <Key className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleCloseSessions(auditor.id, 'auditor')}
                        className="text-orange-600 hover:text-orange-900"
                        title="Cerrar sesión"
                      >
                        <LogOutIcon className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => handleDelete(auditor.id, 'auditor', auditor.full_name)}
                        className="text-red-600 hover:text-red-900"
                        title="Eliminar"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Tab - Mi Perfil (Admin) */}
      {activeTab === 'perfil' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-8 max-w-2xl mx-auto">
          <div className="mb-8">
            <h2 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
              Configuración de la Cuenta Admin
            </h2>
            <p className="text-gray-600 dark:text-gray-400">
              Administra las credenciales de tu cuenta de administrador
            </p>
          </div>

          {/* Cambiar Username */}
          <div className="mb-8 p-6 bg-blue-50 dark:bg-blue-900/20 rounded-lg">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                  Cambiar Usuario (Username)
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Actualiza el nombre de usuario para iniciar sesión
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowChangeUsernameDialog(true)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <Edit className="w-4 h-4" />
              Cambiar Username
            </button>
          </div>

          {/* Cambiar Contraseña */}
          <div className="p-6 bg-green-50 dark:bg-green-900/20 rounded-lg">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-1">
                  Cambiar Contraseña
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  Actualiza tu contraseña para mayor seguridad
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowChangePasswordDialog(true)}
              className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 flex items-center gap-2"
            >
              <Key className="w-4 h-4" />
              Cambiar Contraseña
            </button>
          </div>

          <div className="mt-6 p-4 bg-amber-50 dark:bg-amber-900/20 rounded-lg">
            <p className="text-sm text-amber-800 dark:text-amber-200">
              ⚠️ <strong>Importante:</strong> Guarda tus nuevas credenciales en un lugar seguro. Si olvidas tu contraseña, necesitarás asistencia técnica para recuperar el acceso.
            </p>
          </div>
        </div>
      )}

      {/* Dialog: Create Cliente */}
      {showCreateClienteDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">Crear Nuevo Cliente</h2>
            <form onSubmit={handleCreateCliente}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Nombre
                  </label>
                  <input
                    type="text"
                    required
                    value={clienteForm.name}
                    onChange={(e) => setClienteForm({ ...clienteForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Cartera
                  </label>
                  <select
                    required
                    value={clienteForm.cartera_id}
                    onChange={(e) => setClienteForm({ ...clienteForm, cartera_id: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
                  >
                    <option value="">Seleccionar cartera</option>
                    {carteras.map((cartera) => (
                      <option key={cartera.id} value={cartera.id}>
                        {cartera.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Username
                  </label>
                  <input
                    type="text"
                    required
                    value={clienteForm.username}
                    onChange={(e) => setClienteForm({ ...clienteForm, username: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Contraseña
                  </label>
                  <div className="relative">
                    <input
                      type={showClientePassword ? "text" : "password"}
                      required
                      value={clienteForm.password}
                      onChange={(e) => setClienteForm({ ...clienteForm, password: e.target.value })}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
                    />
                    <button
                      type="button"
                      onClick={() => setShowClientePassword(!showClientePassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    >
                      {showClientePassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Confirmar Contraseña
                  </label>
                  <div className="relative">
                    <input
                      type={showClienteConfirmPassword ? "text" : "password"}
                      required
                      value={clienteForm.confirmPassword}
                      onChange={(e) => setClienteForm({ ...clienteForm, confirmPassword: e.target.value })}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
                    />
                    <button
                      type="button"
                      onClick={() => setShowClienteConfirmPassword(!showClienteConfirmPassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    >
                      {showClienteConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                
                {/* Campo Telegram User ID */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Telegram User ID (Opcional)
                  </label>
                  <input
                    type="text"
                    value={clienteForm.telegram_user_id}
                    onChange={(e) => setClienteForm({ ...clienteForm, telegram_user_id: e.target.value })}
                    placeholder="Ej: 123456789"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    🤖 El cliente debe ejecutar /miid en el bot de Telegram para obtener su ID
                  </p>
                </div>
                
                <div className="bg-blue-50 dark:bg-blue-900 p-3 rounded-lg">
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    ℹ️ El cliente usará estas credenciales para acceder al portal web.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowCreateClienteDialog(false)}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
                >
                  Crear Cliente
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Dialog: Edit Cliente */}
      {showEditClienteDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">Editar Cliente</h2>
            <form onSubmit={handleUpdateCliente}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Nombre
                  </label>
                  <input
                    type="text"
                    required
                    value={clienteForm.name}
                    onChange={(e) => setClienteForm({ ...clienteForm, name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Username (no editable)
                  </label>
                  <input
                    type="text"
                    disabled
                    value={clienteForm.username}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-600 text-gray-500 dark:text-gray-400 cursor-not-allowed"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Nueva Contraseña (dejar en blanco para no cambiar)
                  </label>
                  <div className="relative">
                    <input
                      type={showClientePassword ? "text" : "password"}
                      value={clienteForm.password}
                      onChange={(e) => setClienteForm({ ...clienteForm, password: e.target.value })}
                      className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
                      placeholder="Dejar vacío para no cambiar"
                    />
                    <button
                      type="button"
                      onClick={() => setShowClientePassword(!showClientePassword)}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                    >
                      {showClientePassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                    </button>
                  </div>
                </div>
                {clienteForm.password && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Confirmar Nueva Contraseña
                    </label>
                    <div className="relative">
                      <input
                        type={showClienteConfirmPassword ? "text" : "password"}
                        value={clienteForm.confirmPassword}
                        onChange={(e) => setClienteForm({ ...clienteForm, confirmPassword: e.target.value })}
                        className="w-full px-3 py-2 pr-10 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
                      />
                      <button
                        type="button"
                        onClick={() => setShowClienteConfirmPassword(!showClienteConfirmPassword)}
                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
                      >
                        {showClienteConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Campo Telegram User ID */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Telegram User ID (Opcional)
                  </label>
                  <input
                    type="text"
                    value={clienteForm.telegram_user_id}
                    onChange={(e) => setClienteForm({ ...clienteForm, telegram_user_id: e.target.value })}
                    placeholder="Ej: 123456789"
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-indigo-500 dark:bg-gray-700 dark:text-white"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    🤖 El cliente debe ejecutar /miid en el bot de Telegram para obtener su ID
                  </p>
                </div>
                
                <div className="bg-blue-50 dark:bg-blue-900 p-3 rounded-lg">
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    ℹ️ Se actualizará el nombre y el Telegram User ID. Si deseas cambiar la contraseña, ingresa una nueva.
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditClienteDialog(false);
                    setClienteForm({
                      name: '',
                      cartera_id: '',
                      username: '',
                      password: '',
                      confirmPassword: ''
                    });
                  }}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg"
                >
                  Actualizar Cliente
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Dialog: Create Auditor */}
      {showCreateAuditorDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">Crear Nuevo Auditor</h2>
            <form onSubmit={handleCreateAuditor}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Nombre Completo
                  </label>
                  <input
                    type="text"
                    required
                    value={auditorForm.full_name}
                    onChange={(e) => setAuditorForm({ ...auditorForm, full_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    required
                    value={auditorForm.email}
                    onChange={(e) => setAuditorForm({ ...auditorForm, email: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Username
                  </label>
                  <input
                    type="text"
                    required
                    value={auditorForm.username}
                    onChange={(e) => setAuditorForm({ ...auditorForm, username: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Contraseña
                  </label>
                  <input
                    type="password"
                    required
                    value={auditorForm.password}
                    onChange={(e) => setAuditorForm({ ...auditorForm, password: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Confirmar Contraseña
                  </label>
                  <input
                    type="password"
                    required
                    value={auditorForm.confirmPassword}
                    onChange={(e) => setAuditorForm({ ...auditorForm, confirmPassword: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-purple-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowCreateAuditorDialog(false)}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
                >
                  Crear Auditor
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Dialog: Show Credentials */}
      {showCredentialsDialog && newCredentials && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-lg w-full">
            <div className="flex items-center gap-3 mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
              <h2 className="text-xl font-bold text-gray-900 dark:text-white">
                {newCredentials.type === 'cliente' ? 'Cliente' : 'Auditor'} Creado Exitosamente
              </h2>
            </div>
            
            <div className="space-y-4">
              {newCredentials.type === 'cliente' ? (
                <>
                  <div className="bg-blue-50 dark:bg-blue-900 p-4 rounded-lg">
                    <h3 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">
                      Credenciales de Acceso Web:
                    </h3>
                    <div className="space-y-2 text-sm">
                      <div className="flex justify-between items-center">
                        <span className="text-blue-800 dark:text-blue-200">Usuario:</span>
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-semibold">{newCredentials.username}</span>
                          <button
                            onClick={() => copyToClipboard(newCredentials.username, 'Usuario')}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-blue-800 dark:text-blue-200">Contraseña:</span>
                        <div className="flex items-center gap-2">
                          <span className="font-mono font-semibold">{newCredentials.password}</span>
                          <button
                            onClick={() => copyToClipboard(newCredentials.password, 'Contraseña')}
                            className="text-blue-600 hover:text-blue-800"
                          >
                            <Copy className="w-4 h-4" />
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="bg-green-50 dark:bg-green-900 p-4 rounded-lg">
                    <h3 className="font-semibold text-green-900 dark:text-green-100 mb-2">
                      Credenciales para Telegram:
                    </h3>
                    <div className="space-y-2 text-sm">
                    </div>
                  </div>
                </>
              ) : (
                <div className="bg-purple-50 dark:bg-purple-900 p-4 rounded-lg">
                  <h3 className="font-semibold text-purple-900 dark:text-purple-100 mb-2">
                    Credenciales de Acceso:

                  </h3>
                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between items-center">
                      <span className="text-purple-800 dark:text-purple-200">Usuario:</span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-semibold">{newCredentials.username}</span>
                        <button
                          onClick={() => copyToClipboard(newCredentials.username, 'Usuario')}
                          className="text-purple-600 hover:text-purple-800"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-purple-800 dark:text-purple-200">Contraseña:</span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono font-semibold">{newCredentials.password}</span>
                        <button
                          onClick={() => copyToClipboard(newCredentials.password, 'Contraseña')}
                          className="text-purple-600 hover:text-purple-800"
                        >
                          <Copy className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-purple-800 dark:text-purple-200">URL Login:</span>
                      <span className="font-mono text-xs">/auditor/login</span>
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="flex justify-end gap-3 mt-6">
              <button
                onClick={() => {
                  setShowCredentialsDialog(false);
                  setNewCredentials(null);
                }}
                className="px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Dialog: Reset Password */}
      {showResetPasswordDialog && selectedUser && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">
              Cambiar Contraseña
            </h2>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
              {selectedUser.type === 'cliente' ? 'Cliente' : 'Auditor'}: <strong>{selectedUser.name}</strong>
            </p>
            <form onSubmit={handleResetPassword}>
              <div className="space-y-4">
                <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Username Actual
                  </label>
                  <input
                    type="text"
                    value={selectedUser.username || 'N/A'}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 cursor-not-allowed"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Este es el username actual del usuario
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Nueva Contraseña
                  </label>
                  <input
                    type="password"
                    required
                    value={resetPasswordForm.newPassword}
                    onChange={(e) => setResetPasswordForm({ ...resetPasswordForm, newPassword: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-amber-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Confirmar Contraseña
                  </label>
                  <input
                    type="password"
                    required
                    value={resetPasswordForm.confirmPassword}
                    onChange={(e) => setResetPasswordForm({ ...resetPasswordForm, confirmPassword: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-amber-500 dark:bg-gray-700 dark:text-white"
                  />
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowResetPasswordDialog(false);
                    setResetPasswordForm({ newPassword: '', confirmPassword: '' });
                    setSelectedUser(null);
                  }}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700"
                >
                  Cambiar Contraseña
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Dialog: Cambiar Username del Admin */}
      {showChangeUsernameDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">
              Cambiar Username (Admin)
            </h2>
            <form onSubmit={handleChangeUsername}>
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Nuevo Username
                  </label>
                  <input
                    type="text"
                    required
                    value={changeUsernameForm.newUsername}
                    onChange={(e) => setChangeUsernameForm({ ...changeUsernameForm, newUsername: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                    placeholder="Nuevo username"
                  />
                </div>
                <div className="bg-blue-50 dark:bg-blue-900 p-3 rounded-lg">
                  <p className="text-sm text-blue-800 dark:text-blue-200">
                    ℹ️ Este será tu nuevo username para iniciar sesión como admin
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowChangeUsernameDialog(false);
                    setChangeUsernameForm({ newUsername: '' });
                  }}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Actualizar Username
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Dialog: Cambiar Contraseña del Admin */}
      {showChangePasswordDialog && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white dark:bg-gray-800 rounded-lg p-6 max-w-md w-full">
            <h2 className="text-xl font-bold mb-4 text-gray-900 dark:text-white">
              Cambiar Contraseña (Admin)
            </h2>
            <form onSubmit={handleChangePassword}>
              <div className="space-y-4">
                <div className="bg-gray-50 dark:bg-gray-700 p-3 rounded-lg">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Usuario Actual
                  </label>
                  <input
                    type="text"
                    value={JSON.parse(localStorage.getItem('user') || '{}').username || 'Admin'}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-600 text-gray-700 dark:text-gray-300 cursor-not-allowed"
                  />
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                    Este es tu username actual para iniciar sesión
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Contraseña Actual
                  </label>
                  <input
                    type="password"
                    required
                    value={changePasswordForm.currentPassword}
                    onChange={(e) => setChangePasswordForm({ ...changePasswordForm, currentPassword: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white"
                    placeholder="Ingresa tu contraseña actual"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Nueva Contraseña
                  </label>
                  <input
                    type="password"
                    required
                    value={changePasswordForm.newPassword}
                    onChange={(e) => setChangePasswordForm({ ...changePasswordForm, newPassword: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white"
                    placeholder="Nueva contraseña"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Confirmar Nueva Contraseña
                  </label>
                  <input
                    type="password"
                    required
                    value={changePasswordForm.confirmPassword}
                    onChange={(e) => setChangePasswordForm({ ...changePasswordForm, confirmPassword: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-green-500 dark:bg-gray-700 dark:text-white"
                    placeholder="Confirma la nueva contraseña"
                  />
                </div>
                <div className="bg-amber-50 dark:bg-amber-900 p-3 rounded-lg">
                  <p className="text-sm text-amber-800 dark:text-amber-200">
                    ⚠️ Guarda tu nueva contraseña en un lugar seguro
                  </p>
                </div>
              </div>
              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => {
                    setShowChangePasswordDialog(false);
                    setChangePasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
                  }}
                  className="px-4 py-2 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700"
                >
                  Actualizar Contraseña
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default UserManagement;
